// Per-hour workability for a vessel×operation limit set. Given a forecast and a
// limit (wave, wind, squall sensitivity, task length), returns the per-hour
// probability that both limits are met, plus the future weather windows long
// enough to run the task. Pure: forecast + limit in, numbers out.
//
// Ported from the v1 limitFractions / renderWork logic.

import type { PointForecast } from "../forecast/openmeteo.js";

export interface HourlyWorkability {
  /** per-hour probability (0..1) that both limits are met; null = no data. */
  p: (number | null)[];
  /** contiguous future runs (half-open [start, end)) with p ≥ 0.6 lasting ≥ task hours. */
  windows: { start: number; end: number }[];
}

/**
 * How hard a LIKELY (2) / SEVERE (3) squall hour caps workability, by operation
 * class. Lightning-critical ops (crane lifts, personnel, ROV umbilicals) stand
 * down at lower convective intensity than wind-driven ops; transit barely cares.
 * A tier-1 "possible" squall is advisory shading only — no cap. (Verbatim v1.)
 */
export const SQUALL_CAPS: Record<"low" | "med" | "high", Record<number, number>> = {
  high: { 2: 0.55, 3: 0.25 },
  med: { 2: 0.85, 3: 0.55 },
  low: { 2: 1.0, 3: 0.85 },
};

/**
 * The verdict bands a probability falls into. One list, because the ribbon, its
 * colour key and the track's header meter all have to agree about what 62% means
 * — three copies of these four numbers is how a legend ends up describing a
 * colour the ribbon no longer paints.
 *
 * Ordered high to low: `workGrade` takes the first band the value clears.
 * `varName` is the theme's CSS custom property (the ECDIS tables re-define it per
 * Day/Dusk/Night); `fallback` is only for a canvas drawn before styles resolve.
 */
export interface WorkGrade {
  /** Lowest probability in the band. */
  min: number;
  label: string;
  varName: string;
  fallback: string;
  /** Modifier class for the track meter, styled alongside .tval. */
  cls: string;
}

export const WORK_GRADES: readonly WorkGrade[] = [
  { min: 0.9, label: "Go", varName: "--good", fallback: "#2e8b57", cls: "wk-good" },
  { min: 0.6, label: "Marginal", varName: "--warn", fallback: "#b9791a", cls: "wk-warn" },
  { min: 0.3, label: "Poor", varName: "--serious", fallback: "#d9772b", cls: "wk-serious" },
  { min: 0, label: "No-go", varName: "--crit", fallback: "#b23b3b", cls: "wk-crit" },
];

/** The band a probability (0..1) falls into. Never null — a number always has a
 *  verdict; it is *absence* of a number that must stay uncoloured, and that case
 *  is the caller's to skip before asking. */
export function workGrade(p: number): WorkGrade {
  return WORK_GRADES.find((g) => p >= g.min) ?? WORK_GRADES[WORK_GRADES.length - 1];
}

export interface WorkabilityLimit {
  wave_m: number;
  wind_kn: number;
  squall: "low" | "med" | "high";
  taskHours: number;
}

/**
 * Fraction of the wind ENSEMBLE at hour `i` that sits within `windKn`. Falls
 * back to the fraction of deterministic wind models within limit when members
 * are absent.
 *
 * Exported because the conditions table colours its wind row from exactly this
 * number. The table's wind row and the workability ribbon must never disagree
 * about whether the wind is the binding constraint, and the only way to
 * guarantee that is for both to read ONE function — the ribbon is
 * min(windLimitFractionAt, waveLimitFractionAt) capped in squall hours, so a
 * red wind cell beside a Marginal ribbon hour is arithmetic, not coincidence.
 */
export function windLimitFractionAt(
  fc: PointForecast,
  i: number,
  windKn: number,
): number | null {
  const members = fc.ensembleMembers?.[i];
  if (members && members.length) {
    return members.filter((x) => x <= windKn).length / members.length;
  }
  if (fc.wind) {
    const vals: number[] = [];
    for (const m of fc.wind.models) {
      const v = m.data[i];
      if (v !== null && v !== undefined) vals.push(v);
    }
    if (vals.length) {
      return vals.filter((x) => x <= windKn).length / vals.length;
    }
  }
  return null;
}

/** Fraction of the wave models at hour `i` within `waveM`. The wave half of the
 *  pair above, exported for the same reason. */
export function waveLimitFractionAt(
  fc: PointForecast,
  i: number,
  waveM: number,
): number | null {
  if (!fc.waves) return null;
  const vals: number[] = [];
  for (const m of fc.waves.height) {
    const v = m.data[i];
    if (v !== null && v !== undefined) vals.push(v);
  }
  if (!vals.length) return null;
  return vals.filter((x) => x <= waveM).length / vals.length;
}

/**
 * Where ONE deterministic value sits against its limit, on the same 0..1 scale
 * `workGrade` reads.
 *
 * The fractions above answer "how much of the spread is inside the limit",
 * which needs a spread. A single model's line has none — the Models tab asks a
 * different question of each row: does THIS model breach the limit, and by how
 * much. So the ratio value/limit is mapped onto the grade scale by four stated
 * breakpoints rather than by a fitted curve:
 *
 *   at 0.75× the limit and below → 1.00   comfortably inside
 *   at 1.00× (exactly the limit) → 0.90   the Go/Marginal edge
 *   at 1.50×                     → 0.30   the Poor/No-go edge
 *   at 2.00× and beyond          → 0.00
 *
 * Linear between them. Sitting exactly ON your limit reads as the last Go
 * moment rather than as a breach, which is what a limit means; everything past
 * it falls away quickly, because a 25% wind exceedance is not a 25% problem.
 * The BANDS are WORK_GRADES either way — one table of four numbers still
 * decides what a colour means anywhere in the console.
 */
export const LIMIT_MARGIN_BREAKS: readonly (readonly [number, number])[] = [
  [0.75, 1],
  [1, 0.9],
  [1.5, 0.3],
  [2, 0],
];

export function limitMargin(value: number, limit: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(limit) || limit <= 0) return Number.NaN;
  const r = value / limit;
  const B = LIMIT_MARGIN_BREAKS;
  if (r <= B[0][0]) return B[0][1];
  for (let k = 1; k < B.length; k++) {
    const [r0, p0] = B[k - 1];
    const [r1, p1] = B[k];
    if (r > r1) continue;
    // The lerp written as a weighted average, NOT as p0 + (p1-p0)*f. The two
    // are the same in real arithmetic and not in floating point: the second
    // form returns 0.29999999999999993 at exactly 1.5x the limit, which falls
    // through the Poor band's 0.3 floor and grades a cell No-go one ulp early.
    // The breakpoints above are stated in the help text as exact, so they have
    // to be exact.
    const f = (r - r0) / (r1 - r0);
    return p0 * (1 - f) + p1 * f;
  }
  return B[B.length - 1][1];
}

export function workability(
  fc: PointForecast,
  limit: WorkabilityLimit,
): HourlyWorkability {
  const n = fc.times.length;
  const caps = SQUALL_CAPS[limit.squall];

  const windFrac = (i: number): number | null => windLimitFractionAt(fc, i, limit.wind_kn);
  const waveFrac = (i: number): number | null => waveLimitFractionAt(fc, i, limit.wave_m);

  const p: (number | null)[] = new Array<number | null>(n).fill(null);
  for (let i = 0; i < n; i++) {
    const pWind = windFrac(i);
    const pWave = waveFrac(i);
    if (pWind === null && pWave === null) {
      p[i] = null;
      continue;
    }
    let prob = Math.min(pWind ?? 1, pWave ?? 1);
    // squall cap: a mean-workable hour with squalls actually likely is not a Go
    // hour. Only tiers with a cap entry (2, 3) apply; tier 1 is advisory only.
    const level = fc.squall?.level[i];
    if (level !== undefined && level >= 1) {
      const cap = caps[level];
      if (cap !== undefined) prob = Math.min(prob, cap);
    }
    p[i] = prob;
  }

  // Window finder: contiguous future runs of workable hours (p ≥ 0.6) at least
  // taskHours long, from the current hour onward.
  const windows: { start: number; end: number }[] = [];
  const from = Math.max(fc.nowIndex, 0);
  let start: number | null = null;
  for (let i = from; i <= n; i++) {
    const prob = i < n ? p[i] : null;
    const go = prob !== null && prob >= 0.6;
    if (go && start === null) start = i;
    if (!go && start !== null) {
      if (i - start >= limit.taskHours) windows.push({ start, end: i });
      start = null;
    }
  }

  return { p, windows };
}
